import { fromMarkdown } from 'mdast-util-from-markdown'
import { gfmFromMarkdown } from 'mdast-util-gfm'
import { gfm } from 'micromark-extension-gfm'
import type { Nodes as MdastNode, PhrasingContent } from 'mdast'

/** One markdown block turned into plain text for the read-aloud widget —
 * `markdownToSpeechBlocks`'s unit of output, later split into sentence-sized
 * utterances by `splitIntoUtterances`. */
export interface SpeechBlock {
  text: string
  kind: 'heading' | 'text'
}

/** Node types this extractor never reads text from (read-mode.listen spec
 * §2): embedded images (their alt text isn't spoken), fenced/indented code,
 * raw HTML, and thematic breaks. */
const SILENT_NODE_TYPES = new Set(['image', 'imageReference', 'code', 'html', 'thematicBreak'])

/** Turns phrasing content (the inline children of a paragraph/heading/table
 * cell) into plain text: links resolve to their visible text, bold/italic/
 * strikethrough are unwrapped, inline code keeps its literal text, and a
 * hard line break (`<br>` / trailing double-space) becomes a sentence
 * boundary rather than being dropped, so `splitIntoUtterances`'s
 * `Intl.Segmenter` treats what follows as a new sentence. */
function phrasingText(nodes: readonly PhrasingContent[] | undefined): string {
  if (!nodes) return ''

  let result = ''
  for (const node of nodes) {
    switch (node.type) {
      case 'text':
      case 'inlineCode':
        result += node.value
        break
      case 'break':
        result += '. '
        break
      case 'image':
      case 'imageReference':
        // Alt text is never read aloud.
        break
      case 'strong':
      case 'emphasis':
      case 'delete':
      case 'link':
      case 'linkReference':
        result += phrasingText(node.children)
        break
      default:
        break
    }
  }
  return result
}

function hasChildren(node: MdastNode): node is MdastNode & { children: MdastNode[] } {
  return 'children' in node && Array.isArray(node.children)
}

function collect(node: MdastNode, blocks: SpeechBlock[]): void {
  if (SILENT_NODE_TYPES.has(node.type)) return

  // `heading`/`paragraph`/`tableCell` are narrowed by their literal
  // `node.type` here (rather than through the generic `hasChildren` guard)
  // specifically so `node.children` keeps its precise `PhrasingContent[]`
  // type — no cast needed.
  if (node.type === 'heading' || node.type === 'paragraph') {
    const text = phrasingText(node.children).trim()
    if (text) blocks.push({ text, kind: node.type === 'heading' ? 'heading' : 'text' })
    return
  }
  if (node.type === 'tableCell') {
    const text = phrasingText(node.children).trim()
    if (text) blocks.push({ text, kind: 'text' })
    return
  }

  if (hasChildren(node)) {
    for (const child of node.children) collect(child, blocks)
  }
}

/**
 * Extracts the plain-text blocks a markdown field's rendered article would
 * show as separate paragraphs/headings/list items/quotes/table cells — the
 * source `buildSpeechSegments` segments into sentence-sized utterances.
 * Ignores embedded images, code, raw HTML and thematic breaks (nothing a
 * listener would want read aloud).
 */
export function markdownToSpeechBlocks(markdown: string | null | undefined): SpeechBlock[] {
  const trimmed = (markdown ?? '').trim()
  if (!trimmed) return []

  const tree = fromMarkdown(trimmed, {
    extensions: [gfm()],
    mdastExtensions: [gfmFromMarkdown()],
  })

  const blocks: SpeechBlock[] = []
  collect(tree, blocks)
  return blocks
}
